import React, { useMemo } from 'react';
import { Empty } from 'antd';
import { isEmpty } from 'lodash';
import moment from 'moment-timezone';

import DualAxisAreaChart from '../../../../../../shared/designSystem/components/DualAxisAreaChart';
import { useTimezone } from '../../../../../../../core/hooks/useTimezone';

// =============================================================================
// SignUpsChart (V1) — facelifted off BizCharts/AntD-Card chrome to the shared
// `DualAxisAreaChart` primitive (single-axis mode via `rightY="None"`).
//
// Picker has been lifted to the parent `SignUpsLayout` Performance card
// head (segmented presets via `<CampaignRangeSelector/>`), matching the
// V2 SignUps / Summary / Display pattern. This component is now a pure
// chart body — receives `data` (full chart rows) + `dataSignUpRangeDate`
// (the filtered subset selected by the picker) and renders whichever has
// content.
//
// Data shape — incoming rows use { xAxis, count }. Normalized inline to
// { xAxis: YYYY-MM-DD, signUps } so DualAxisAreaChart's `labelToKey`
// ("Sign Ups" → "signUps") resolves correctly.
// =============================================================================

const SignUpsChart = ({ data, dataSignUpRangeDate }) => {
  const { timezone: effectiveTimezone = 'UTC' } = useTimezone();

  const toTimezoneMoment = (date, format) => {
    if (format) {
      return effectiveTimezone === 'UTC'
        ? moment.utc(date, format)
        : moment.tz(date, format, effectiveTimezone);
    }
    return effectiveTimezone === 'UTC'
      ? moment.utc(date)
      : moment.tz(date, effectiveTimezone);
  };

  // Source rows: pick the filtered subset when a range is active, else full
  // dataset. Sort ascending so the chart line draws left-to-right.
  const sourceRows = useMemo(() => {
    const base =
      dataSignUpRangeDate && dataSignUpRangeDate.length > 0 ? dataSignUpRangeDate : data;
    if (isEmpty(base)) return [];
    return [...base].sort((a, b) => {
      if (toTimezoneMoment(a.xAxis).isBefore(toTimezoneMoment(b.xAxis))) return -1;
      return 1;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dataSignUpRangeDate, effectiveTimezone]);

  // Normalize { xAxis, count } -> { xAxis: YYYY-MM-DD, signUps } for
  // DualAxisAreaChart's labelToKey ("Sign Ups" -> "signUps").
  const chartRows = useMemo(
    () =>
      sourceRows.map((row) => ({
        xAxis: moment(row.xAxis).isValid()
          ? moment(row.xAxis).format('YYYY-MM-DD')
          : row.xAxis,
        signUps: Number(row.count) || 0,
      })),
    [sourceRows]
  );

  const hasData = data && data.length > 1;

  return (
    <div className="campaign-overview__chart">
      <div className="campaign-overview__chart-head">
        <div className="campaign-overview__chart-eyebrow mono-eyebrow">
          Sign Ups by Date
        </div>
      </div>

      <div className="campaign-overview__chart-body">
        {isEmpty(data) ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Please refresh your browser to receive the latest updates."
          />
        ) : hasData ? (
          <DualAxisAreaChart
            dualChartData={chartRows}
            leftY="Sign Ups"
            rightY="None"
          />
        ) : null}
      </div>
    </div>
  );
};

export default SignUpsChart;
